import { useMemo, useState } from "react";
import { categories, feed, type FeedItem } from "@/mocks/homeData";

interface LatestSectionProps {
  activeCat: string;
  onCatChange: (id: string) => void;
}

export default function LatestSection({ activeCat, onCatChange }: LatestSectionProps) {
  const tabs = [{ id: "all", name: "全部动态", icon: "ri-layout-grid-line" }, ...categories];
  const [query, setQuery] = useState("");

  const list = useMemo<FeedItem[]>(() => {
    const kw = query.trim().toLowerCase();
    return feed.filter((f) => {
      const okCat = activeCat === "all" || f.cat === activeCat;
      const okKw =
        !kw ||
        f.title.toLowerCase().includes(kw) ||
        f.summary.toLowerCase().includes(kw) ||
        f.author.toLowerCase().includes(kw);
      return okCat && okKw;
    });
  }, [activeCat, query]);

  return (
    <section id="latest" className="bg-background-50">
      <div className="mx-auto max-w-7xl px-4 py-12 md:px-6 md:py-16">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <span className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-widest text-primary-600">
              <i className="ri-refresh-line" />
              Latest Feed
            </span>
            <h2 className="mt-2 font-heading text-2xl font-black text-foreground-950 md:text-3xl">
              最新动态追踪
            </h2>
            <p className="mt-1 max-w-xl text-sm text-foreground-700">
              按栏目浏览主播、达人、博主与大V的最新一手观察，支持关键词即时检索。
            </p>
          </div>
          <label className="flex h-11 w-full max-w-sm items-center gap-2 rounded-full border border-background-300 bg-background-50 px-4 transition-colors focus-within:border-primary-400 focus-within:ring-2 focus-within:ring-primary-100">
            <i className="ri-search-line text-foreground-500" />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="搜索标题 / 人物 / 关键词"
              className="w-full bg-transparent text-sm text-foreground-950 outline-none placeholder:text-foreground-400"
              aria-label="搜索动态"
            />
            {query && (
              <button
                type="button"
                onClick={() => setQuery("")}
                className="text-foreground-400 transition-colors hover:text-foreground-700"
                aria-label="清空搜索"
              >
                <i className="ri-close-circle-fill" />
              </button>
            )}
          </label>
        </div>

        <div className="no-scrollbar mt-6 flex gap-2 overflow-x-auto pb-1 md:flex-wrap md:overflow-visible">
          {tabs.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => onCatChange(t.id)}
              className={`inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                activeCat === t.id
                  ? "bg-primary-500 text-background-50"
                  : "border border-background-300 bg-background-50 text-foreground-700 hover:border-primary-300 hover:text-primary-700"
              }`}
            >
              <i className={t.icon} />
              {t.name}
            </button>
          ))}
        </div>

        <div className="mt-3 text-xs text-foreground-500">
          找到 <span className="font-semibold text-primary-600">{list.length}</span> 条{activeCat === "all" ? "" : "相关"}动态
        </div>

        {list.length === 0 ? (
          <div className="mt-6 flex flex-col items-center rounded-3xl border border-dashed border-background-300 bg-background-100/60 px-6 py-16 text-center">
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-200 text-foreground-500">
              <i className="ri-search-eye-line text-2xl" />
            </span>
            <p className="mt-4 font-medium text-foreground-800">没有匹配的动态</p>
            <p className="mt-1 text-sm text-foreground-600">换个关键词，或点击上方栏目切换试试</p>
            <button
              type="button"
              onClick={() => {
                setQuery("");
                onCatChange("all");
              }}
              className="mt-4 inline-flex h-9 items-center whitespace-nowrap rounded-full bg-primary-500 px-5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              查看全部动态
            </button>
          </div>
        ) : (
          <div className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {list.map((item) => {
              const meta = categories.find((c) => c.id === item.cat);
              return (
                <article
                  key={item.id}
                  className="group flex flex-col overflow-hidden rounded-2xl border border-background-200 bg-background-50 transition-all hover:border-primary-200"
                >
                  <a href="#latest" className="relative block h-44 w-full overflow-hidden">
                    <img
                      src={item.image}
                      alt={item.title}
                      className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                    />
                    {item.isHot && (
                      <span className="absolute left-3 top-3 inline-flex items-center gap-1 rounded-full bg-accent-500 px-2.5 py-1 text-[11px] font-bold text-background-50">
                        <i className="ri-fire-fill" />
                        热度高
                      </span>
                    )}
                  </a>
                  <div className="flex flex-1 flex-col p-4">
                    <div className="flex flex-wrap items-center gap-2 text-xs">
                      <span className="rounded-md bg-primary-50 px-2 py-0.5 font-medium text-primary-700 ring-1 ring-primary-100">
                        {meta?.name}
                      </span>
                      <span className="inline-flex items-center gap-1 text-foreground-500">
                        <i className="ri-time-line" />
                        {item.timeLabel}
                      </span>
                    </div>
                    <h3 className="mt-2.5 font-heading text-base font-bold leading-snug text-foreground-950">
                      <a href="#latest" className="line-clamp-2 transition-colors group-hover:text-primary-700">
                        {item.title}
                      </a>
                    </h3>
                    <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-foreground-600">
                      {item.summary}
                    </p>
                    <div className="mt-4 flex flex-wrap items-center justify-between gap-2 border-t border-background-200 pt-3 text-xs text-foreground-600">
                      <span className="inline-flex items-center gap-1.5">
                        <span className="flex h-6 w-6 items-center justify-center rounded-full bg-secondary-100 text-secondary-800">
                          <i className="ri-user-3-line text-xs" />
                        </span>
                        {item.author}
                      </span>
                      <span className="inline-flex items-center gap-3">
                        <span className="inline-flex items-center gap-1">
                          <i className="ri-eye-line" />
                          {item.views}
                        </span>
                        <span className="inline-flex items-center gap-1">
                          <i className="ri-thumb-up-line" />
                          {item.likes}
                        </span>
                      </span>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}